/**
 * healthcareImportColumns: the one list of HEALTHCARE import columns.
 * ───────────────────────────────────────────────────────────────────────────
 * Every reader of a pharmacy workbook takes its columns from here:
 *
 *   - healthcareImport.js turns whatever header a sheet carries into the field
 *     key it means (canonicalHeader), so "Price (UGX)" and "price_ugx" both
 *     work.
 *   - healthcareImportTemplate.js writes these labels, hints and formulas into
 *     the workbook the Template button downloads.
 *
 * SAME BLUEPRINT AS EVERY OTHER VERTICAL. The China Town workbook is the shape
 * Izi settled on for brand uploads on 26 Sep 2026 and the one every other
 * vertical follows: a READ ME, a Column Guide, '1. Sections', '2. Products'
 * with a hint line above each Title Case header, and a '3. Lists' of reference
 * values. Everything that layout is made of comes from
 * catalogueImportColumns.js, so a pharmacist who has seen a brand sheet is
 * looking at the same sheet with different fine print.
 *
 * WHAT A PHARMACY HAS THAT A BRAND DOES NOT. Founder decision, 27 Sep 2026: a
 * pharmacy sells both in bulk and at retail, so healthcare carries the same
 * commercial layer a brand does (Min Order Qty, Price on Request). On top of
 * that it carries the things only a regulated seller has: whether an item is a
 * service rather than a product, whether it needs a prescription, a minimum
 * age, the controlled-substance flag and its schedule, the VAT treatment, and
 * the batch and expiry register the counter dispenses from.
 *
 * WHAT IS DELIBERATELY ABSENT, AND WHY IT MUST STAY ABSENT:
 *
 *   NO TIER PRICE COLUMNS. Brand has Retailer / Wholesaler / Healthcare price
 *   columns. Healthcare has none, and that omission is load-bearing rather
 *   than an oversight. models/healthcareSectionProductModel.js spreads
 *   `tradePricingFields({ tiers: false })` on purpose:
 *   services/pricing/orderLinePricingService.js resolves a healthcare line
 *   from `row.price` alone and then OVERWRITES the line with it, so a
 *   `tierPrices` object on a healthcare product would be silently re-priced at
 *   the retail base price on every wholesale order, with a logger.warn as the
 *   only trace. That is a money leak created by ADDING A FIELD. An importer
 *   column is exactly how such a field would arrive in bulk. Tier prices land
 *   here in the slice that makes the pricing phase tier-aware, together, or
 *   not at all. __tests__/healthcareCommercialLayer.test.js guards the schema
 *   half; healthcareImport.test.js guards this half.
 *
 *   NO ORDER FLOW COLUMN. `orderFlowOverride` is a brand field. A pharmacy
 *   order has no branded-packaging flow to override.
 *
 *   NO SPEC COLUMNS. The healthcare catalogue has no `specs` field, so a
 *   `spec:` column would parse into something the server drops on the floor.
 *   healthcareImport.js switches spec parsing off rather than letting an
 *   operator fill a column that does nothing.
 *
 * Headers are Title Case words. The snake_case keys import too, so a sheet
 * exported from a pharmacy's own stock system keeps working.
 */
import {
  IDENTITY_COLUMNS,
  MEDIA_COLUMNS,
  MERCHANDISING_COLUMNS,
  NOTES_COLUMN,
  PAYMENT_COLUMN,
  PRICING_COLUMNS,
  ROLE,
  SECTION_COLUMNS,
  STOCK_COLUMNS,
  col,
  createHeaderResolver,
  makeProductRange,
} from "./catalogueImportColumns.js";

export {
  NAMES,
  PLACEHOLDER,
  PRODUCTS_SHEET,
  PRODUCT_FORMULAS,
  ROLE,
  ROLE_LABEL,
  SECTIONS_SHEET,
  SECTION_COLUMNS,
  SECTION_FORMULAS,
  STATUS,
  columnLetter,
  columnLetters,
  headerKey,
  specHeader,
} from "./catalogueImportColumns.js";

/** healthcareProductSchema.productType. */
export const PRODUCT_TYPES = Object.freeze(["PRODUCT", "SERVICE"]);

/** models/shared/productTaxFields.js PRODUCT_VAT_CATEGORIES, mirrored. */
export const VAT_CATEGORIES = Object.freeze(["STANDARD", "ZERO_RATED", "EXEMPT"]);

/** How one batch is written in the Batches cell, and what separates two. */
export const BATCH_FIELD_SEPARATOR = "|";
export const BATCH_EXAMPLE = "BN-4471 | 2027-03-31 | 120";

export const PRODUCT_TYPE_COLUMN = col(
  "product_type",
  "Product or Service",
  ROLE.OPTIONAL,
  "Product for something on a shelf, Service for a consultation. Blank means Product.",
  {
    aliases: ["type", "item_type"],
    detail:
      "A Product is counted in Count in Stock. A Service is counted in Available Slots. Filling the wrong one of those two is the commonest mistake on this sheet, so the To Do column will not catch it for you.",
  }
);

export const VAT_COLUMN = col(
  "vat_category",
  "VAT Treatment",
  ROLE.OPTIONAL,
  "STANDARD, ZERO_RATED or EXEMPT. Blank means STANDARD.",
  {
    aliases: ["vat", "tax_category"],
    detail:
      "A fiscal question and nothing else. Never set it from whether an item needs a prescription: a prescription-only drug is standard-rated unless your accountant says otherwise, and getting this wrong takes 18% VAT off every sale of it.",
  }
);

export const AVAILABLE_SLOTS_COLUMN = col(
  "available_slots",
  "Available Slots",
  ROLE.OPTIONAL,
  "Appointments a service can take. Blank means 10.",
  {
    aliases: ["slots", "appointment_slots"],
    detail: "Only read for a Service. Leave it blank on a Product.",
  }
);

/** The regulated fine print. Grouped so the sheet reads in one sweep. */
export const REGULATION_COLUMNS = [
  col(
    "requires_prescription",
    "Prescription Only",
    ROLE.OPTIONAL,
    "Yes when a buyer must produce a prescription.",
    {
      aliases: ["prescription", "prescription_required"],
      detail: "A clinical fact, not a tax one. It does not change the VAT Treatment column.",
    }
  ),
  col(
    "age_restriction",
    "Minimum Age",
    ROLE.OPTIONAL,
    "Lowest age allowed to buy. Blank or 0 means no limit.",
    { aliases: ["age_limit", "minimum_age"] }
  ),
  col(
    "controlled_substance",
    "Controlled Substance",
    ROLE.OPTIONAL,
    "Yes when the regulator treats this as controlled.",
    { aliases: ["controlled"] }
  ),
  col(
    "controlled_schedule",
    "Schedule",
    ROLE.OPTIONAL,
    "The regulator's schedule, e.g. II or III. Blank when not controlled.",
    {
      aliases: ["control_schedule", "drug_schedule"],
      detail:
        "Free text on purpose: schedules are set per country regulator, and Lusuku records what yours calls it rather than forcing it into a list.",
    }
  ),
  col(
    "batches",
    "Batches",
    ROLE.OPTIONAL,
    `Batch number, expiry and quantity. Write one as ${BATCH_EXAMPLE}.`,
    {
      aliases: ["batch", "lots", "batch_register"],
      detail:
        "Put more than one batch on separate lines in the same cell, or separate them with a semicolon. The expiry must be a date, written YYYY-MM-DD. The counter prints the batch number and expiry on the receipt and records them in the controlled-substance log, so a batch that cannot be read is refused rather than quietly dropped.",
    }
  ),
];

/**
 * Product columns in the order a person fills them in: who it is, what it
 * costs and how it is taxed, whether it can be bought, what it looks like, how
 * it is found, then the regulated fine print. The order is the China Town
 * blueprint's and must not be reshuffled: the workbook's formulas address
 * columns by letter.
 */
export const PRODUCT_COLUMNS = [
  ...IDENTITY_COLUMNS,
  PRODUCT_TYPE_COLUMN,
  ...PRICING_COLUMNS,
  VAT_COLUMN,
  ...STOCK_COLUMNS,
  AVAILABLE_SLOTS_COLUMN,
  ...MEDIA_COLUMNS,
  ...MERCHANDISING_COLUMNS,
  ...REGULATION_COLUMNS,
  PAYMENT_COLUMN,
  NOTES_COLUMN,
];

const resolver = createHeaderResolver({
  sections: SECTION_COLUMNS,
  products: PRODUCT_COLUMNS,
});

export const COLUMNS_BY_KIND = resolver.COLUMNS_BY_KIND;

/** The field key a header means on a healthcare sheet. Unknown headers pass through. */
export const canonicalHeader = resolver.canonicalHeader;

/** The Title Case label for a key, for messages an operator reads. */
export const labelOf = resolver.labelOf;

/** Builds P(key) for SECTION_FORMULAS: a product column over rows first..last. */
export const productRange = makeProductRange(PRODUCT_COLUMNS);
